import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, ArrowRight, HeartHandshake, Sparkles, RefreshCw, Loader2, Users, Check } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "nc_love_test_v1";

const CATEGORIES = [
  { key: "mind", name: "마음의 준비", intent: "과거의 상처를 정리하고 새로운 사랑을 받아들일 준비가 되었는지 확인합니다.", range: [1, 4] },
  { key: "self", name: "자기 이해", intent: "자신의 연애 성향, 욕구, 강점과 약점을 얼마나 잘 알고 있는지 확인합니다.", range: [5, 7] },
  { key: "choice", name: "상대 선택력", intent: "나에게 맞는 사람을 건강한 기준으로 선택할 수 있는지 확인합니다.", range: [8, 10] },
  { key: "comm", name: "감정 소통력", intent: "호감과 불편함을 건강하게 표현하고 상대의 말을 들을 수 있는지 확인합니다.", range: [11, 14] },
  { key: "action", name: "만남 실행력", intent: "새로운 만남을 만들고 실제 행동으로 옮길 수 있는지 확인합니다.", range: [15, 17] },
  { key: "sustain", name: "관계 유지력", intent: "차이를 조율하고 관계를 안정적으로 이어갈 수 있는지 확인합니다.", range: [18, 20] },
];

const QUESTIONS = [
  "과거의 이별이나 결혼생활을 떠올려도 감정적으로 크게 흔들리지 않는다.",
  "외로움을 피하기 위해서가 아니라, 좋은 사람과 건강한 관계를 만들기 위해 연애하고 싶다.",
  "새로운 사람을 과거의 연인이나 배우자와 비교하지 않을 자신이 있다.",
  "연애를 시작하면 상처받을 수도 있다는 사실을 받아들일 수 있다.",
  "나는 연애할 때 어떤 장점과 단점을 보이는지 알고 있다.",
  "상대방의 관심이나 평가가 없어도 나의 가치를 인정할 수 있다.",
  "나는 어떤 관계를 원하고, 어떤 관계는 원하지 않는지 설명할 수 있다.",
  "내가 원하는 상대의 기준이 외모나 경제력 외에도 분명하게 정리되어 있다.",
  "강하게 끌리는 사람과 나에게 잘 맞는 사람의 차이를 구별할 수 있다.",
  "상대방의 말보다 행동과 태도를 보고 사람을 판단하는 편이다.",
  "호감이 있는 사람에게 부담스럽지 않게 관심을 표현할 수 있다.",
  "서운하거나 불편한 일이 생겼을 때 참기만 하거나 화내지 않고 말할 수 있다.",
  "상대방의 말을 바로 판단하거나 반박하지 않고 끝까지 들을 수 있다.",
  "상대방에게 원하는 것이 있을 때 눈치채기를 기다리지 않고 직접 말할 수 있다.",
  "새로운 사람을 만날 수 있는 모임이나 활동에 참여할 의향이 있다.",
  "마음에 드는 사람이 생기면 상대방의 연락만 기다리지 않고 작은 행동을 할 수 있다.",
  "몇 번의 거절이나 실패를 경험해도 다시 만남을 시도할 수 있다.",
  "연애를 시작해도 나의 일상, 친구, 취미를 건강하게 유지할 수 있다.",
  "상대방이 나와 다른 생각이나 생활방식을 가지고 있어도 존중할 수 있다.",
  "갈등이 생겼을 때 헤어짐부터 생각하기보다 함께 해결할 방법을 찾으려 한다.",
];

const OPTIONS = [
  { label: "전혀 그렇지 않다", value: 1 },
  { label: "그렇지 않은 편이다", value: 2 },
  { label: "보통이다", value: 3 },
  { label: "그런 편이다", value: 4 },
  { label: "매우 그렇다", value: 5 },
];

const categoryForQ = (qNum) => CATEGORIES.find((c) => qNum >= c.range[0] && qNum <= c.range[1]);

export default function LoveAssessmentPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [step, setStep] = useState("intro"); // intro | quiz | loading | result
  const [current, setCurrent] = useState(1);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  // 새로고침 후에도 진행 상태 유지
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (saved) {
        setAnswers(saved.answers || {});
        if (saved.completed && saved.result) {
          setResult(saved.result);
          setStep("result");
        } else if (Object.keys(saved.answers || {}).length > 0) {
          setCurrent(saved.current || 1);
          setStep("quiz");
        }
      }
    } catch { /* noop */ }
  }, []);

  const persist = (patch) => {
    try {
      const prev = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prev, ...patch }));
    } catch { /* noop */ }
  };

  const selectAnswer = (val) => {
    const next = { ...answers, [current]: val };
    setAnswers(next);
    persist({ answers: next, current });
  };

  const allAnswered = Object.keys(answers).length === 20;

  const goNext = () => {
    if (current < 20) {
      const n = current + 1;
      setCurrent(n);
      persist({ current: n });
    }
  };
  const goPrev = () => {
    if (current > 1) {
      const n = current - 1;
      setCurrent(n);
      persist({ current: n });
    }
  };

  const submit = async () => {
    setStep("loading");
    let res = null;
    try {
      const r = await fetch(`${API_URL}/api/love-test/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers }),
      });
      if (r.ok) res = await r.json();
    } catch { /* network fail → fallback below */ }
    if (!res) res = clientFallback(answers);
    setResult(res);
    setStep("result");
    persist({ answers, current: 20, completed: true, result: res });
    // 로그인 사용자는 계정에 저장
    if (token) {
      try {
        await fetch(`${API_URL}/api/love-test/save`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ...res, answers }),
        });
      } catch { /* noop */ }
    }
  };

  const restart = () => {
    setAnswers({});
    setResult(null);
    setCurrent(1);
    localStorage.removeItem(STORAGE_KEY);
    setStep("intro");
  };

  const handleFindMatch = () => {
    if (!user) { navigate("/?auth=login"); return; }
    navigate("/members");
  };

  // ---------------- Render ----------------
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAF6F1] to-[#F3ECE4] py-6 px-4">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-stone-500 hover:text-stone-700 text-sm mb-4" data-testid="love-test-back">
          <ArrowLeft className="w-4 h-4" /> 뒤로
        </button>

        {step === "intro" && <IntroScreen onStart={() => { setStep("quiz"); setCurrent(Object.keys(answers).length ? current : 1); }} hasProgress={Object.keys(answers).length > 0} />}
        {step === "quiz" && (
          <QuizScreen
            current={current} answers={answers} onSelect={selectAnswer}
            onNext={goNext} onPrev={goPrev} onSubmit={submit} allAnswered={allAnswered}
          />
        )}
        {step === "loading" && (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm" data-testid="love-test-loading">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-burgundy-500" />
            <p className="text-stone-600 mt-4 text-sm">AI가 당신의 연애 역량을 분석하고 있어요...</p>
          </div>
        )}
        {step === "result" && result && (
          <ResultScreen result={result} onFindMatch={handleFindMatch} onRestart={restart} />
        )}
      </div>
    </div>
  );
}

const IntroScreen = ({ onStart, hasProgress }) => (
  <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm text-center" data-testid="love-test-intro">
    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-burgundy-50 mb-4">
      <HeartHandshake className="w-7 h-7 text-burgundy-600" />
    </div>
    <h1 className="font-serif text-2xl sm:text-3xl font-bold bg-gradient-to-r from-burgundy-700 to-rose-500 bg-clip-text text-transparent">나도 연애할 수 있을까?</h1>
    <p className="text-stone-600 mt-3 text-sm sm:text-base leading-relaxed">나의 연애 역량을 진단하고<br />나에게 꼭 맞는 실전 팁을 받아보세요.</p>
    <p className="text-xs text-stone-400 mt-2">총 20문항 · 약 3분 소요</p>

    <div className="text-left bg-stone-50 rounded-xl p-4 mt-6 space-y-2">
      <p className="text-[12.5px] text-stone-600 leading-relaxed">좋아 보이는 답보다 <b>현재 나의 모습과 가장 가까운 답</b>을 선택할수록 정확한 결과를 받을 수 있습니다.</p>
      <p className="text-[12.5px] text-stone-500 leading-relaxed">좋은 인연을 만나는 일은 단순히 운에만 달려 있지 않습니다. 나의 연애 성향을 이해하고 필요한 부분을 준비할수록, 나에게 맞는 사람을 알아보고 건강한 관계로 이어갈 가능성이 높아집니다.</p>
      <p className="text-[12.5px] text-stone-500 leading-relaxed">이 테스트는 당신을 평가하기 위한 것이 아니라, 좋은 인연을 만나기 위해 지금 무엇을 준비하고 실천해야 하는지 알려드리기 위한 테스트입니다.</p>
    </div>

    <div className="flex flex-wrap justify-center gap-1.5 mt-6">
      {CATEGORIES.map((c) => (
        <span key={c.key} className="text-[11px] font-medium text-burgundy-700 bg-burgundy-50 px-2.5 py-1 rounded-full">{c.name}</span>
      ))}
    </div>

    <button onClick={onStart} className="mt-6 w-full py-3.5 rounded-xl bg-burgundy-600 hover:bg-burgundy-700 text-white font-bold transition-colors" data-testid="love-test-begin-btn">
      {hasProgress ? "이어서 진단하기" : "진단 시작하기"}
    </button>
  </div>
);

const QuizScreen = ({ current, answers, onSelect, onNext, onPrev, onSubmit, allAnswered }) => {
  const cat = categoryForQ(current);
  const selected = answers[current];
  const progress = Math.round((Object.keys(answers).length / 20) * 100);
  const isLast = current === 20;
  return (
    <div className="bg-white rounded-2xl p-5 sm:p-7 shadow-sm" data-testid="love-test-quiz">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold text-burgundy-700 bg-burgundy-50 px-2.5 py-1 rounded-full" data-testid="quiz-category">{cat?.name}</span>
        <span className="text-xs text-stone-400" data-testid="quiz-progress-num">{current} / 20</span>
      </div>
      <p className="text-[12px] text-stone-500 leading-snug mb-3">{cat?.intent}</p>
      <div className="w-full h-1.5 bg-stone-100 rounded-full overflow-hidden mb-5">
        <div className="h-full bg-gradient-to-r from-burgundy-500 to-rose-400 transition-all" style={{ width: `${progress}%` }} />
      </div>

      <p className="font-serif text-[16px] sm:text-[18px] font-semibold text-stone-800 leading-relaxed mb-5 min-h-[3.5rem]">{QUESTIONS[current - 1]}</p>

      <div className="space-y-2">
        {OPTIONS.map((o) => (
          <button
            key={o.value}
            onClick={() => onSelect(o.value)}
            className={`w-full text-left px-4 py-3 rounded-xl border text-[14px] font-medium transition-all ${
              selected === o.value
                ? "border-burgundy-500 bg-burgundy-50 text-burgundy-800"
                : "border-stone-200 hover:border-burgundy-300 text-stone-600"
            }`}
            data-testid={`quiz-option-${o.value}`}
          >
            <span className="inline-flex items-center gap-2">
              <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${selected === o.value ? "border-burgundy-500 bg-burgundy-500" : "border-stone-300"}`}>
                {selected === o.value && <Check className="w-3 h-3 text-white" />}
              </span>
              {o.label}
            </span>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between mt-6 gap-3">
        <button onClick={onPrev} disabled={current === 1} className="flex items-center gap-1 px-4 py-2.5 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-600 text-sm font-medium disabled:opacity-40" data-testid="quiz-prev-btn">
          <ArrowLeft className="w-4 h-4" /> 이전
        </button>
        {isLast ? (
          <button onClick={onSubmit} disabled={!allAnswered} className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-burgundy-600 hover:bg-burgundy-700 text-white text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed" data-testid="quiz-submit-btn">
            <Sparkles className="w-4 h-4" /> 결과 확인하기
          </button>
        ) : (
          <button onClick={onNext} disabled={!selected} className="flex items-center gap-1 px-5 py-2.5 rounded-xl bg-burgundy-600 hover:bg-burgundy-700 text-white text-sm font-bold disabled:opacity-40" data-testid="quiz-next-btn">
            다음 <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

const ResultScreen = ({ result, onFindMatch, onRestart }) => {
  const areas = result.areas || [];
  return (
    <div className="space-y-4" data-testid="love-test-result">
      <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm text-center">
        <h1 className="font-serif text-2xl sm:text-3xl font-bold bg-gradient-to-r from-burgundy-700 to-rose-500 bg-clip-text text-transparent">당신도 충분히 연애할 수 있습니다</h1>
        <div className="inline-flex items-baseline gap-1 mt-4">
          <span className="text-5xl font-bold text-burgundy-700" data-testid="result-total">{result.total}</span>
          <span className="text-stone-400 text-lg">/ 100</span>
        </div>
        <p className="mt-3 inline-block text-[14px] font-bold text-burgundy-800 bg-burgundy-50 px-3 py-1.5 rounded-full" data-testid="result-type">{result.typeTitle}</p>
      </div>

      {/* AI 진단 */}
      <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm">
        <div className="flex items-center gap-1.5 mb-2">
          <Sparkles className="w-4 h-4 text-burgundy-600" />
          <span className="text-[11px] font-bold text-burgundy-700 tracking-wider uppercase">AI 개인 진단</span>
        </div>
        <p className="text-[14px] text-stone-700 leading-relaxed" data-testid="result-diagnosis">{result.diagnosis}</p>
        <div className="grid gap-2 mt-4">
          <div className="bg-emerald-50 rounded-xl p-3">
            <p className="text-[11px] font-bold text-emerald-700 mb-0.5">나의 강점</p>
            <p className="text-[13px] text-stone-700">{result.strength}</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-3">
            <p className="text-[11px] font-bold text-amber-700 mb-0.5">가장 필요한 변화</p>
            <p className="text-[13px] text-stone-700">{result.mainChange}</p>
          </div>
        </div>
      </div>

      {/* 5단계 로드맵 */}
      <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm">
        <h3 className="font-serif text-[16px] font-bold text-stone-800 mb-3">나를 위한 5단계 실전 로드맵</h3>
        <ol className="space-y-2.5">
          {(result.roadmap || []).map((r, i) => (
            <li key={i} className="flex gap-3 items-start" data-testid={`result-roadmap-${i}`}>
              <span className="shrink-0 w-6 h-6 rounded-full bg-burgundy-600 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
              <span className="text-[13.5px] text-stone-700 leading-snug pt-0.5">{r}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* 영역 그래프 */}
      <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm">
        <h3 className="font-serif text-[15px] font-bold text-stone-800 mb-4">6개 영역 진단</h3>
        <div className="space-y-3">
          {areas.map((a) => (
            <div key={a.key} data-testid={`result-area-${a.key}`}>
              <div className="flex justify-between text-[12.5px] mb-1">
                <span className="text-stone-600 font-medium">{a.name}</span>
                <span className="text-burgundy-700 font-bold">{a.score}점</span>
              </div>
              <div className="w-full h-2.5 bg-stone-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-burgundy-500 to-rose-400 transition-all" style={{ width: `${a.score}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-2">
        <button onClick={onFindMatch} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-burgundy-600 hover:bg-burgundy-700 text-white font-bold transition-colors" data-testid="result-find-match-btn">
          <Users className="w-4 h-4" /> 나와 잘 맞는 인연 만나보기
        </button>
        <button onClick={onRestart} className="w-full flex items-center justify-center gap-1.5 py-3 rounded-xl bg-white border border-stone-300 text-stone-600 font-medium hover:bg-stone-50 transition-colors" data-testid="result-restart-btn">
          <RefreshCw className="w-4 h-4" /> 테스트 다시 하기
        </button>
      </div>

      <p className="text-[11.5px] text-stone-400 leading-relaxed text-center px-2 pb-4">
        진단은 끝이 아니라 새로운 인연을 위한 시작입니다.<br />
        뉴커플링은 단순히 사람을 보여주는 곳이 아니라, 나를 이해하고 좋은 사람을 알아보며 건강한 관계를 만들어갈 수 있도록 돕는 중년 싱글을 위한 연애 플랫폼입니다.
      </p>
    </div>
  );
};

// AI/네트워크 완전 실패 시 클라이언트 최종 안전장치 (규칙 기반)
function clientFallback(answers) {
  const val = (i) => Math.max(0, Math.min(5, parseInt(answers[i] || 0, 10)));
  const total = Array.from({ length: 20 }, (_, i) => val(i + 1)).reduce((a, b) => a + b, 0);
  const areas = CATEGORIES.map((c) => {
    const qs = [];
    for (let i = c.range[0]; i <= c.range[1]; i++) qs.push(val(i));
    const raw = qs.reduce((a, b) => a + b, 0);
    const max = qs.length * 5;
    return { key: c.key, name: c.name, raw, max, score: Math.round((raw / max) * 100) };
  });
  const ordered = [...areas].sort((a, b) => a.score - b.score);
  const low = ordered[0], high = ordered[ordered.length - 1];
  return {
    total, stage: "", areas,
    typeTitle: `${high.name}은 강하지만 ${low.name}이(가) 필요한 유형`,
    diagnosis: `당신의 가장 큰 강점은 '${high.name}'입니다. 다만 '${low.name}' 영역이 지금 인연을 만나는 데 가장 큰 걸림돌이 되고 있어요. 이 부분을 조금씩 준비하면 좋은 관계로 이어질 가능성이 훨씬 높아집니다.`,
    strength: `${high.name} 영역이 특히 안정적입니다.`,
    mainChange: `${low.name} 영역을 작은 실천부터 준비해보세요.`,
    roadmap: [
      "오늘 나의 연애에서 바꾸고 싶은 점 세 가지를 적어보세요.",
      "이번 주 원하는 상대의 필수 조건과 불가 조건을 각각 세 가지 정하세요.",
      "7일 안에 새로운 모임이나 대화에 한 번 참여하세요.",
      "관심 있는 사람에게 먼저 짧은 안부나 구체적인 칭찬을 표현하세요.",
      "만남 후 상대의 반응보다 내가 편안하고 존중받았는지를 기록하세요.",
    ],
  };
}
